/**
 * The Slow-Interaction record (concept §5) as an L2 drawer — the unit
 * everything else on the console is built from, rendered in the §5 order:
 * header line, identity, F&O timings against their baselines, the network
 * block (or the explicit UNATTRIBUTED chip), exceptions, classification with
 * its reason, and the two next-diagnostic actions.
 *
 * Open state lives in the URL (`?activity=<activity_id>`), so a record is a
 * link someone can paste into a ticket. Opening one is a per-user call and is
 * audit-logged; it carries the session's reason like the user view.
 *
 * Every failure has words: loading is a skeleton, a 400 says the reason was
 * missing, a 404 says the record is not there, an unreadable workspace shows
 * the access panel. Nothing here renders an empty block as though it were a
 * clean one.
 */

import { ChevronDown, Copy, Icon } from "@norican/icons";
import {
  Alert,
  Button,
  Collapsible,
  DescriptionList,
  EmptyState,
  SegmentBar,
  Skeleton,
  Typography,
  useToastManager,
} from "@norican/react";
import { useState } from "react";

import { DetailDrawer } from "@/components/board";
import { type FoConsoleRecord, type FoConsoleSlowRow, useFoConsoleRecord } from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";
import { logger } from "@/lib/logger";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { ClassificationChip, JoinKindChip, NetStateChip, RumTierChip } from "./console-chips";
import {
  apiErrorDetail,
  apiErrorStatus,
  deriveRecordReasonParts,
  deviceClassText,
  type FoConsoleRumBlock,
  formatDurationMs,
  formatOrigin,
  formatPctWhole,
  formatShare,
  hasNetworkBlock,
  hasRumV2,
  hasRumV3,
  JOIN_KIND_DETAIL,
  JOIN_KIND_LABEL,
  machineLineUp,
  NOT_REPORTED,
  parseNetState,
  presentClassification,
  rowOrigin,
  rowRum,
  RUM_TIER_EVIDENCE,
  RUM_TIER_THRESHOLDS,
  rumJoinKind,
  rumTierFromBlock,
  UNATTRIBUTED_CHIP,
} from "./console-format";
import { formatBinDateTime, formatCount } from "./status";

/** Fixed colours for the two halves of the form's duration as the browser saw it. */
const RUM_SPLIT_CONFIG = identityConfig(["server_ms", "browser_ms"], {
  server_ms: "waiting on F&O",
  browser_ms: "in the browser",
});

/** The exact line a record without browser telemetry shows. */
const NO_RUM_LINE = "no browser telemetry for this user";

interface RecordDrawerProps {
  /** The open record's `activity_id`, or `null` when the drawer is closed. */
  activityId: string | null;
  /** The session's reason — required for the call. */
  reason: string | null;
  onClose: () => void;
  /** Drill path back up, root first. */
  breadcrumbs: { label: string; to?: string }[];
}

export function RecordDrawer({ activityId, reason, onClose, breadcrumbs }: RecordDrawerProps) {
  const record = useFoConsoleRecord(activityId, reason);
  const open = activityId != null;
  const data = record.data;
  const row = data?.row ?? null;

  const title = row ? row.form : activityId ? "Slow-interaction record" : "";
  const status = apiErrorStatus(record.error);

  return (
    <DetailDrawer open={open} onClose={onClose} title={title} breadcrumbs={breadcrumbs}>
      {!reason && (
        <Alert tone="warning" title="No reason for this session">
          A record is a per-user view. Enter a reason on the page behind this drawer; the backend
          refuses the call without one.
        </Alert>
      )}

      {record.error && status === 400 && (
        <Alert tone="warning" title="The backend refused the call: reason missing or too short">
          {apiErrorDetail(record.error) ??
            "Every per-user call carries a reason of at least four characters. Change it on the page and reopen the record."}
        </Alert>
      )}
      {record.error && status === 404 && (
        <Alert tone="neutral" title="No record with this activity id">
          {apiErrorDetail(record.error) ??
            "Either the page view was not slow by the §5 rule, or it has aged past the workspace's 90-day retention. Nothing was fabricated to fill the gap."}
        </Alert>
      )}
      {record.error && status !== 400 && status !== 404 && (
        <Alert tone="danger" title="Couldn't load the record">
          {status === 0
            ? "The NOVA API did not respond. This is a NOVA problem, not an Azure one."
            : (apiErrorDetail(record.error) ??
              `The NOVA API answered ${status ?? "with an error"}.`)}
        </Alert>
      )}

      {record.isPending && open && Boolean(reason) && !data && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton height="5rem" />
          <Skeleton height="8rem" />
          <Skeleton height="6rem" />
        </div>
      )}

      {data && !isPanelOk(data) && <FoAccessPanel panel="Slow-interaction record" state={data} />}

      {data && isPanelOk(data) && (
        <>
          {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}
          {row ? (
            <RecordBody record={data} row={row} />
          ) : (
            <EmptyState
              size="sm"
              title="The workspace holds no record for this activity"
              description="The activity id resolved to nothing slow. Not an error — and not a zero."
            />
          )}
        </>
      )}
    </DetailDrawer>
  );
}

interface RecordBodyProps {
  record: FoConsoleRecord;
  row: FoConsoleSlowRow;
}

function RecordBody({ record, row }: RecordBodyProps) {
  const identity = record.identity;
  const diagnostic = record.next_diagnostic;
  const reasonParts = deriveRecordReasonParts(row);
  const networkVisible = hasNetworkBlock(row);
  const origin = rowOrigin(row);
  const rum = rowRum(row);
  const chip = presentClassification(row.classification);

  return (
    <div className="flex flex-col gap-5">
      {/* ── Header line: ts · form · entity ─────────────────────────────── */}
      <div className="flex flex-col gap-1">
        <Typography variant="body-sm" tone="muted" as="p">
          <span className="tabular-nums">{formatBinDateTime(row.ts)}</span> · {row.form} · legal
          entity {row.legal_entity}
        </Typography>
        <div className="flex flex-wrap items-center gap-2">
          <ClassificationChip classification={row.classification} size="sm" />
          <NetStateChip netState={row.net_state} size="sm" />
        </div>
      </div>

      {/* ── Identity ─────────────────────────────────────────────────────── */}
      <Section title="User">
        <DescriptionList
          columns={1}
          items={[
            { label: "Name", value: identity?.display_name ?? "—" },
            { label: "Mail", value: identity?.mail ?? "—" },
            { label: "F&O alias", value: identity?.alias ?? "—" },
            { label: "Account kind", value: identity?.account_kind ?? "—" },
            {
              label: "Site",
              value: networkVisible
                ? `${row.site}${row.src_type ? ` (${row.src_type})` : ""}`
                : UNATTRIBUTED_CHIP,
            },
            {
              // Entra's placement — the only location an unattributed row has,
              // and the axis the board's "Where the unattributed sit" ranks by.
              label: "Country · office (Entra)",
              value: origin ? formatOrigin(origin) : "—",
            },
            {
              label: "PoP · ISP",
              value: networkVisible ? `${row.pop ?? "—"} · ${row.isp ?? "—"}` : "—",
            },
            { label: "Device", value: networkVisible ? (row.device ?? "—") : "—" },
            { label: "AOS · session", value: `${row.aos} · ${row.session_id}` },
          ]}
        />
      </Section>

      {/* ── F&O timings against baselines ────────────────────────────────── */}
      <Section title="F&O">
        <DescriptionList
          columns={1}
          items={[
            {
              label: "F&O total",
              value: (
                <Typography variant="body" as="span" tabular className="font-semibold">
                  {formatDurationMs(row.duration_ms)}
                </Typography>
              ),
            },
            {
              label: "Form p50 / p95 (28 d, this entity)",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(row.form_p50_ms)} / {formatDurationMs(row.form_p95_ms)}
                </span>
              ),
            },
            {
              label: "Peers, same 15 min",
              value:
                row.peer_users == null || row.peer_users === 0 ? (
                  "no other user ran this form"
                ) : (
                  <span className="tabular-nums">
                    {formatDurationMs(row.peer_p50_ms)} ({formatCount(row.peer_users)} other users)
                  </span>
                ),
            },
            {
              label: "Activity id",
              value: <code className="text-xs break-all">{row.activity_id}</code>,
            },
          ]}
        />
      </Section>

      {/* ── Network block, or the explicit chip ──────────────────────────── */}
      <Section title="Network (Cato, same 15-minute bucket)">
        {networkVisible ? (
          <DescriptionList
            columns={1}
            items={[
              {
                label: "TCP connect",
                value: <span className="tabular-nums">{formatDurationMs(row.tcp_ms)}</span>,
              },
              {
                label: "TLS connect",
                value: <span className="tabular-nums">{formatDurationMs(row.tls_ms)}</span>,
              },
              {
                label: "TTFB",
                value: (
                  <span className="tabular-nums">
                    {formatDurationMs(row.ttfb_ms)}
                    <span className="text-fg-muted">
                      {" "}
                      · site 7-day p50 {formatDurationMs(row.site_ttfb_p50_ms)}
                    </span>
                  </span>
                ),
              },
              { label: "Verdict", value: <NetStateChip netState={row.net_state} size="sm" /> },
            ]}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <NetStateChip netState="unattributed" size="sm" />
            <Typography variant="body-sm" tone="muted">
              No Cato row for this person in this bucket, so TCP, TLS and TTFB are unknown — not
              fine. Coverage gap, never a verdict. Peer comparison is the only network evidence.
            </Typography>
          </div>
        )}
      </Section>

      {/* ── Browser block (RUM extension), or the explicit absence ───────── */}
      <Section title="Browser (RUM extension, the interaction's minute)">
        {rum ? (
          <RumBlock rum={rum} row={row} networkVisible={networkVisible} />
        ) : (
          <div className="flex flex-col gap-1">
            <Typography variant="body-sm" className="font-medium">
              {NO_RUM_LINE}
            </Typography>
            <Typography variant="body-sm" tone="muted">
              The fo-rum extension is not installed or not reporting for this person, so server
              wait, main-thread time and endpoint RTT are unknown — not zero.
            </Typography>
          </div>
        )}
      </Section>

      {/* ── Exceptions ───────────────────────────────────────────────────── */}
      <Section title="F&O exceptions in this activity">
        {row.exceptions.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            none
          </Typography>
        ) : (
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
            {row.exceptions.map((exception) => (
              <li key={exception}>
                <code className="text-xs break-all">{exception}</code>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ── Classification + reason ──────────────────────────────────────── */}
      <Section title="Classification">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <ClassificationChip classification={row.classification} size="sm" />
            {chip.detail && (
              <Typography variant="body-sm" as="span">
                {chip.detail}
              </Typography>
            )}
          </div>
          <Typography
            variant="caption"
            tone="muted"
            as="p"
            className="font-semibold tracking-wide uppercase"
          >
            Reason
          </Typography>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
            {reasonParts.map((part) => (
              <li key={part}>{part}</li>
            ))}
          </ul>
        </div>
      </Section>

      {/* ── Next diagnostic ──────────────────────────────────────────────── */}
      <Section title="Next diagnostic">
        {diagnostic ? (
          <NextDiagnostic
            performanceTimerUrl={diagnostic.performance_timer_url}
            traceSteps={diagnostic.trace_steps}
          />
        ) : (
          <Typography variant="body-sm" tone="muted">
            The backend attached no next-diagnostic for this record.
          </Typography>
        )}
      </Section>

      <Typography variant="caption" tone="muted">
        Coverage: network layer {networkVisible ? "present (user behind Cato)" : "not visible"} ·
        browser telemetry{" "}
        {rum
          ? `present (RUM extension, ${hasRumV2(rum) ? "schema v2" : "schema v1 — the v2 columns are unknown, not zero"}, ${
              JOIN_KIND_LABEL[rumJoinKind(rum)]
            })`
          : "absent"}{" "}
        · device metrics: the extension&apos;s own CPU and memory sampling when the minute carries
        it, Cato CMA otherwise.
      </Typography>
    </div>
  );
}

interface RumBlockProps {
  rum: FoConsoleRumBlock;
  /** The record itself — the F&O duration the browser's wait is set against, and Cato's numbers. */
  row: FoConsoleSlowRow;
  /** Whether Cato has a row for this person in this bucket, so a comparison is possible. */
  networkVisible: boolean;
}

/**
 * The browser's half of the record, schema v2.
 *
 * It opens with the join kind, because that decides what the rest is worth: an
 * activity-id match is this interaction's own telemetry, while the 15-minute
 * fallback may hold a neighbouring request the same person made. Then the split
 * between waiting on F&O and time in the browser, the tier function v4 named
 * (when one fired), the endpoint's own network breakdown beside Cato's numbers,
 * what the browser measured of interaction and rendering, the device, and the
 * errors it counted.
 *
 * Absence is stated everywhere. A minute written by extension v0.1 carries none
 * of the v2 columns, and every one of them renders as an em dash with a line
 * saying the version is why — never as a zero, which would read as "no long
 * frames, no errors, idle CPU".
 *
 * Privacy: what appears here is what the concept permits and no more. The
 * long-animation-frame attribution is a **script path** on the app host, the
 * JavaScript error is a **count and a template hash** — the message text never
 * left the browser — and nothing on this panel names a URL, a title or a field
 * value.
 */
function RumBlock({ rum, row, networkVisible }: RumBlockProps) {
  const durationMs = row.duration_ms;
  const serverMs = rum.server_ms ?? 0;
  const overflow = serverMs > durationMs && durationMs > 0;
  const total = overflow ? serverMs : durationMs;
  const inBrowserMs = Math.max(0, durationMs - serverMs);
  const serverShare = durationMs > 0 ? Math.min(1, serverMs / durationMs) : null;

  const joinKind = rumJoinKind(rum);
  const v2 = hasRumV2(rum);
  // The probe tier only fires against a normal Cato path, or none at all —
  // exactly as function v4 has it, and as the reason text derives it.
  const catoNormal = !networkVisible || parseNetState(row.net_state) === "normal";
  const tier = rumTierFromBlock(rum, durationMs, catoNormal);

  /** Cato's mean for the same bucket, or the words for why there is none. */
  const catoCell = (value: number | null) =>
    networkVisible ? (
      <span className="text-fg-muted tabular-nums"> · Cato {formatDurationMs(value)}</span>
    ) : (
      <span className="text-fg-subtle"> · Cato: no row</span>
    );

  return (
    <div className="flex flex-col gap-4">
      {/* ── How this minute was joined to this interaction ───────────────── */}
      <div className="flex flex-col gap-1">
        <JoinKindChip joinKind={joinKind} size="sm" />
        <Typography variant="caption" tone="muted">
          {JOIN_KIND_DETAIL[joinKind]}
        </Typography>
      </div>

      {/* ── Server wait vs time in the browser ───────────────────────────── */}
      {rum.server_ms != null && durationMs > 0 ? (
        <div className="flex flex-col gap-1">
          <SegmentBar
            size="sm"
            total={total}
            showLegend
            aria-label={`Of the ${formatDurationMs(durationMs)} form duration, the longest single F&O response wait was ${formatDurationMs(
              serverMs,
            )}, leaving ${formatDurationMs(inBrowserMs)} in the browser`}
            segments={[
              {
                value: serverMs,
                label: `longest F&O wait ${formatDurationMs(serverMs)}`,
                color: RUM_SPLIT_CONFIG.server_ms?.color,
              },
              {
                value: inBrowserMs,
                label: `in the browser ${formatDurationMs(inBrowserMs)}`,
                color: RUM_SPLIT_CONFIG.browser_ms?.color,
              },
            ]}
          />
          <Typography variant="caption" tone="muted">
            {overflow
              ? `The longest F&O wait (${formatDurationMs(serverMs)}) exceeds the form duration (${formatDurationMs(
                  durationMs,
                )}): the minute holds a request that was not this one activity.`
              : `${serverShare != null ? Math.round(serverShare * 100) : "—"}% of the F&O duration is covered by the longest single F&O response wait (p95 request duration in that minute).`}
          </Typography>
        </div>
      ) : (
        <Typography variant="body-sm" tone="muted">
          The browser reported this minute but measured no same-host request durations, so the
          server/browser split is unknown.
        </Typography>
      )}

      {/* ── The v4 tier this minute's own numbers support ────────────────── */}
      {tier && (
        <div className="flex flex-col gap-1">
          <RumTierChip kind={tier} size="sm" />
          <Typography variant="caption" tone="muted">
            {RUM_TIER_EVIDENCE[tier]}.
          </Typography>
        </div>
      )}

      {/* ── The endpoint's own path, beside Cato's ───────────────────────── */}
      <SubSection title="Endpoint network breakdown (browser p50 · Cato mean)">
        <DescriptionList
          columns={2}
          items={[
            {
              label: "DNS",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.dns_p50_ms)}
                  <span className="text-fg-subtle"> · Cato: not measured</span>
                </span>
              ),
            },
            {
              label: "TCP connect",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.connect_p50_ms)}
                  {catoCell(row.tcp_ms)}
                </span>
              ),
            },
            {
              label: "TLS",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.tls_p50_ms)}
                  {catoCell(row.tls_ms)}
                </span>
              ),
            },
            {
              label: "TTFB",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.ttfb_p50_ms)}
                  {catoCell(row.ttfb_ms)}
                </span>
              ),
            },
            {
              label: "Requests · new connections",
              value: (
                <span className="tabular-nums">
                  {formatCount(rum.requests)} · {formatCount(rum.new_connections ?? null)}
                </span>
              ),
            },
            {
              label: "HTTP/3 · from cache",
              value: (
                <span className="tabular-nums">
                  {formatShare(rum.h3_share)} · {formatShare(rum.cache_hit_share)}
                </span>
              ),
            },
          ]}
        />
        <Typography variant="caption" tone="muted">
          The browser measures the endpoint&apos;s own leg of the path; Cato measures the tunnel
          from the site. They answer different questions and are never averaged together. DNS has no
          Cato equivalent in the DEM feed.
        </Typography>
      </SubSection>

      {/* ── What the browser felt: interactions and rendering ────────────── */}
      <SubSection title="Interaction and rendering">
        <DescriptionList
          columns={2}
          items={[
            {
              label: "Interaction p50 · max",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.interaction_p50_ms)} /{" "}
                  {formatDurationMs(rum.interaction_max_ms)}
                </span>
              ),
            },
            {
              label: "Interactions counted",
              value: (
                <span className="tabular-nums">{formatCount(rum.interaction_count ?? null)}</span>
              ),
            },
            {
              label: "Longest animation frame",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.loaf_max_ms)}
                  {rum.loaf_total_ms != null && (
                    <span className="text-fg-muted">
                      {" "}
                      · {formatDurationMs(rum.loaf_total_ms)} total
                    </span>
                  )}
                </span>
              ),
            },
            {
              label: "Main thread busy (long tasks)",
              value: <span className="tabular-nums">{formatDurationMs(rum.long_task_ms)}</span>,
            },
            {
              label: "Frame attributed to",
              value: rum.loaf_top_script ? (
                <code className="text-xs break-all">{rum.loaf_top_script}</code>
              ) : (
                "— (no script attribution)"
              ),
            },
          ]}
        />
        <Typography variant="caption" tone="muted">
          Script attribution is a path on the app host — no origin, no query, no file the user
          opened.
        </Typography>
      </SubSection>

      {/* ── The device and its transport ─────────────────────────────────── */}
      <SubSection title="Device and transport">
        <DescriptionList
          columns={2}
          items={[
            {
              label: "CPU",
              value: <span className="tabular-nums">{formatPctWhole(rum.cpu_pct)}</span>,
            },
            {
              label: "Memory used",
              value: <span className="tabular-nums">{formatPctWhole(rum.mem_used_pct)}</span>,
            },
            {
              label: "RTT · connection",
              value: (
                <span className="tabular-nums">
                  {formatDurationMs(rum.rtt_ms)} · {rum.conn_type || "—"}
                </span>
              ),
            },
            { label: "Browser", value: rum.browser || "—" },
          ]}
        />
        <Typography variant="caption" tone="muted">
          CPU and memory are the browser&apos;s own sampled readings while an app tab was visible,
          averaged over the minute — not a device inventory, and not per-tab activity.
        </Typography>
      </SubSection>

      {/* ── The machine underneath it (schema v3) ────────────────────────── */}
      <SubSection title="Machine" value={deviceClassText(rum)}>
        <DescriptionList
          columns={2}
          items={machineLineUp(rum).map((line) => ({
            label: line.label,
            value: (
              <span
                className={
                  line.warn ? "text-warning-fg font-semibold tabular-nums" : "tabular-nums"
                }
              >
                {line.value}
                {line.warn && (
                  <span className="font-normal">
                    {" "}
                    — at or above {RUM_TIER_THRESHOLDS.heapPct} % of the ceiling
                  </span>
                )}
              </span>
            ),
          }))}
        />
        <Typography variant="caption" tone="muted">
          What the browser could say about the machine it ran on. Every line is{" "}
          <em>{NOT_REPORTED}</em> when it said nothing — never 0 GB, never an invented device. Power
          saving is derived from the platform&apos;s hints, not read from an OS setting. There is no
          compliance or disk figure here: those are Intune facts NOVA cannot read.
        </Typography>
      </SubSection>

      {/* ── What went wrong in the browser ───────────────────────────────── */}
      <SubSection title="Errors the browser counted">
        <DescriptionList
          columns={2}
          items={[
            {
              label: "Responses 4xx · 5xx",
              value: (
                <span className="tabular-nums">
                  {formatCount(rum.status_4xx ?? null)} · {formatCount(rum.status_5xx ?? null)}
                </span>
              ),
            },
            {
              label: "JavaScript errors",
              value: <span className="tabular-nums">{formatCount(rum.js_errors ?? null)}</span>,
            },
            {
              label: "Top error template",
              value: rum.js_error_top_hash ? (
                <code className="text-xs">{rum.js_error_top_hash}</code>
              ) : (
                "—"
              ),
            },
            {
              label: "Idle probe to the app host (TTFB)",
              value: <span className="tabular-nums">{formatDurationMs(rum.probe_ttfb_ms)}</span>,
            },
          ]}
        />
        <Typography variant="caption" tone="muted">
          Error messages never leave the browser: the hash is a digest of the message with digits,
          quotes and URLs stripped, so two people hitting the same defect share a hash and nobody
          reads the text. Group by it, do not try to read it.
        </Typography>
      </SubSection>

      {!v2 && (
        <Typography variant="caption" tone="muted">
          This minute carries none of the schema v2 measurements, so the breakdown, interaction,
          device, error and probe rows above are unknown rather than zero — the browser that posted
          it runs extension v0.1.
        </Typography>
      )}

      {v2 && !hasRumV3(rum) && (
        <Typography variant="caption" tone="muted">
          This minute carries the v2 measurements but none of the schema v3 host signals, so every
          Machine line reads &ldquo;{NOT_REPORTED}&rdquo;: the browser that posted it runs an
          extension below v0.3, or the workspace function is still v4. The machine is undescribed,
          not idle.
        </Typography>
      )}
    </div>
  );
}

/**
 * A labelled group inside a drawer section — one step quieter than `Section`.
 *
 * `value` puts one reading on the heading line itself, which the Machine
 * line-up uses for the function's own device label: it is the summary of the
 * six lines below it, so repeating it as a seventh row would be noise.
 */
function SubSection({
  title,
  value,
  children,
}: {
  title: string;
  value?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <Typography variant="body-sm" as="h4" className="font-semibold">
          {title}
        </Typography>
        {value != null && (
          <Typography variant="body-sm" tone="muted" as="span">
            {value}
          </Typography>
        )}
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <Typography
        variant="caption"
        tone="muted"
        as="h3"
        className="font-semibold tracking-wide uppercase"
      >
        {title}
      </Typography>
      {children}
    </section>
  );
}

interface NextDiagnosticProps {
  performanceTimerUrl: string;
  traceSteps: string[];
}

function NextDiagnostic({ performanceTimerUrl, traceSteps }: NextDiagnosticProps) {
  const toast = useToastManager();
  const [stepsOpen, setStepsOpen] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(performanceTimerUrl);
      toast.add({
        title: "Copied",
        description: "Performance Timer URL is on the clipboard — open it in the user's browser.",
        type: "success",
      });
    } catch (error) {
      logger.warn("[fo-console] clipboard write failed", error);
      toast.add({
        title: "Couldn't copy",
        description: performanceTimerUrl,
        type: "error",
        timeout: 0,
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          startIcon={<Icon icon={Copy} size="xs" />}
          onClick={() => void copy()}
        >
          Copy Performance Timer URL
        </Button>
        <Typography variant="caption" tone="muted">
          <code className="break-all">{performanceTimerUrl}</code>
        </Typography>
      </div>
      <Typography variant="caption" tone="muted">
        Reproduce with <code>&amp;debug=develop</code> to split client from server time. Debug mode
        slows the session: a reproduction tool, not a monitor.
      </Typography>
      <Collapsible.Root open={stepsOpen} onOpenChange={setStepsOpen}>
        <Collapsible.Trigger className="text-fg inline-flex items-center gap-2 rounded text-sm font-medium hover:underline focus-visible:outline-2">
          <Icon
            icon={ChevronDown}
            size="sm"
            aria-hidden="true"
            className={
              stepsOpen
                ? "rotate-180 motion-safe:transition-transform"
                : "motion-safe:transition-transform"
            }
          />
          Trace capture steps ({traceSteps.length})
        </Collapsible.Trigger>
        <Collapsible.Panel>
          {traceSteps.length === 0 ? (
            <Typography variant="body-sm" tone="muted" className="mt-2">
              No steps attached.
            </Typography>
          ) : (
            <ol className="mt-2 flex list-decimal flex-col gap-1 pl-5 text-sm">
              {traceSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  );
}
